import { Location } from "@angular/common";
import {
	Component,
	ElementRef,
	Inject,
	OnInit,
	ViewChild,
} from "@angular/core";
import {
	MSAL_GUARD_CONFIG,
	MsalBroadcastService,
	MsalGuardConfiguration,
	MsalService,
} from "@azure/msal-angular";
import {
	AuthenticationResult,
	BrowserUtils,
	InteractionStatus,
	PopupRequest,
	RedirectRequest,
} from "@azure/msal-browser";
import { TranslateService } from "@ngx-translate/core";
import { Subject } from "rxjs";
import { filter, takeUntil } from "rxjs/operators";
import { environment } from "src/environments/environment";
import { UserProfileViewModel } from "./_core/models/main/user-profile.model";
import { HeaderService } from "./_core/providers/header.service";
import { ApiEnum, HttpDataService } from "./_core/providers/http-data.service";
import { UserProfileStore } from "./_core/stores/user-profile.store";
import { slideInAnimation } from "./route-animations";

@Component({
	selector: "app-root",
	templateUrl: "./app.component.html",
	styleUrls: ["./app.component.scss"],
	animations: [slideInAnimation],
})
export class AppComponent implements OnInit {
	title = "acciona-network";
	@ViewChild("landingAppContainer") landingContainer: ElementRef;

	isIframe = false;
	loginDisplay = false;
	loaded = false;
	private readonly _destroying$ = new Subject<void>();

	constructor(
		private readonly translateService: TranslateService,
		private readonly headerService: HeaderService,
		private readonly httpDataService: HttpDataService,
		private readonly userProfileStore: UserProfileStore,
		//private authService: AuthService,
		@Inject(MSAL_GUARD_CONFIG)
		private readonly msalGuardConfig: MsalGuardConfiguration,
		private readonly authService: MsalService,
		private readonly msalBroadcastService: MsalBroadcastService,
		private readonly location: Location
	) {
		this.translateService.setDefaultLang("es");
		this.translateService.use("es");
	}

	ngOnInit() {
		this.clearStaleAccessTokens();
		this.userProfileStore.clearUserProfile();

		const currentPath = this.location.path();
		// Dont perform nav if in iframe or popup, other than for front-channel logout
		this.isIframe =
			BrowserUtils.isInIframe() &&
			!window.opener &&
			currentPath.indexOf("logout") < 0; // Remove this line to use Angular Universal
		this.setLoginDisplay();

		this.msalBroadcastService.inProgress$
			.pipe(
				filter(
					(status: InteractionStatus) =>
						status === InteractionStatus.None
				),
				takeUntil(this._destroying$)
			)
			.subscribe(() => {
				this.setLoginDisplay();
				this.checkAndSetActiveAccount();
			});

		const promise = this.headerService.getPlaces();
	}

	setLanguage(language: string) {
		this.translateService.use(language);
	}

	setLoginDisplay() {
		this.loginDisplay =
			this.authService.instance.getAllAccounts().length > 0;
	}

	async checkAndSetActiveAccount() {
		/**
		 * If no active account set but there are accounts signed in, sets first account to active account
		 * To use active account set here, subscribe to inProgress$ first in your component
		 * Note: Basic usage demonstrated. Your app may require more complicated account selection logic
		 */
		const activeAccount = this.authService.instance.getActiveAccount();

		if (
			!activeAccount &&
			this.authService.instance.getAllAccounts().length > 0
		) {
			const accounts = this.authService.instance.getAllAccounts();
			this.authService.instance.setActiveAccount(accounts[0]);
		}

		try {
			const user = await this.httpDataService
				.get$<UserProfileViewModel>(
					ApiEnum.API_ROOT,
					environment.endpoint.settings.user
				)
				.toPromise();
			if (user && user.username && user.username.length > 0) {
				this.userProfileStore.setUserProfile(user);
				this.loaded = true;
			} else {
				if (!environment.production) {
					console.log("no tengo usuario");
				}
			}
		} catch (error) {
			if (!environment.production) {
				console.log(error);
			}
		}

		this.httpDataService
			.get$<UserProfileViewModel>(
				ApiEnum.API_ROOT,
				environment.endpoint.settings.user
			)
			.subscribe(
				(user) => {
					if (user && user.username && user.username.length > 0) {
						this.userProfileStore.setUserProfile(user);
					} else {
						if (!environment.production) {
							console.log("no tengo usuario");
						}
					}
				},
				(error) => {
					if (!environment.production) {
						console.log(error);
					}
				}
			);
	}

	loginRedirect() {
		if (this.msalGuardConfig.authRequest) {
			this.authService.loginRedirect({
				...this.msalGuardConfig.authRequest,
			} as RedirectRequest);
		} else {
			this.authService.loginRedirect();
		}
	}

	loginPopup() {
		if (this.msalGuardConfig.authRequest) {
			this.authService
				.loginPopup({
					...this.msalGuardConfig.authRequest,
				} as PopupRequest)
				.subscribe((response: AuthenticationResult) => {
					this.authService.instance.setActiveAccount(
						response.account
					);
				});
		} else {
			this.authService
				.loginPopup()
				.subscribe((response: AuthenticationResult) => {
					this.authService.instance.setActiveAccount(
						response.account
					);
				});
		}
	}

	logout(popup?: boolean) {
		if (popup) {
			this.authService.logoutPopup({
				mainWindowRedirectUri: "/",
			});
		} else {
			this.authService.logoutRedirect();
		}
	}

	ngOnDestroy(): void {
		this._destroying$.next(undefined);
		this._destroying$.complete();
	}

	private clearStaleAccessTokens() {
		const oneWeekInMilliseconds = 7 * 24 * 60 * 60 * 1000;
		const currentTime = Date.now();

		let cachedAt: number = 1695631234567; // Reemplaza este valor con el tuyo
		let expiresOn: number = 1695631234567; // Reemplaza este valor con el tuyo

		for (let i = 0; i < localStorage.length; i++) {
			const key = localStorage.key(i);
			if (key.includes("-accesstoken-")) {
				const tokenData = JSON.parse(localStorage.getItem(key));

				if (tokenData && tokenData.cachedAt) {
					const tokenTime = tokenData.cachedAt;

					cachedAt = tokenData.cachedAt;
					expiresOn = tokenData.expiresOn;
					// Convertir a una fecha y hora legible
					const fechaHoraCached: Date = new Date(cachedAt * 1000);

					// Obtener la fecha y hora actual
					const fechaHoraActual: Date = new Date();

					const unDia = 24 * 60 * 60 * 1000; // Milisegundos en un día
					const diferencia = Math.abs(
						fechaHoraCached.getTime() - fechaHoraActual.getTime()
					);

					// Verificar si la diferencia es mayor a una semana (7 días)
					if (diferencia > 7 * unDia) {
						if (!environment.production) {
							console.log(
								"El token lleva más de una semana de diferencia."
							);
						}
						localStorage.clear();
						if (!environment.production) {
							console.log(
								"Local storage cleared due to expired token"
							);
						}
						return;
					} else {
						if (!environment.production) {
							console.log(
								"El token lleva menos de una semana de diferencia."
							);
						}
					}
				}
			}
		}
		if (!environment.production) {
			console.log("No expired tokens found");
		}
	}
}
